import Scrollbars from "react-custom-scrollbars-2";
import PdfViewer from "../PdfViewer/PdfViewer";
import "./ArchModal.scss";
import { useEffect, useState, useRef } from "react";

const formatTime = (secs) => {
  const m = Math.floor(secs / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(secs % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
};

const ArchModal = ({ item, onClose }) => {
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    if (item) {
      console.log("Item Keys:", Object.keys(item));
      console.log("Metadata:", item.metadata);
      console.log("Collection:", item.collection);
    }
  }, [item]);

  useEffect(() => {
    if (!showVideoPlayer && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [showVideoPlayer]);

  if (!item) return null;

  const handleMediaClick = () => {
    if (item.isPdf && item.pdfUrl) {
      window.open(item.pdfUrl, "_blank");
    } else if (item.isVideo && item.videoUrl) {
      setShowVideoPlayer(true);
    } else if (item.imageUrl) {
      window.open(item.imageUrl, "_blank");
    }
  };

  const handleCloseVideo = (e) => {
    e.stopPropagation();
    setShowVideoPlayer(false);
    setCurrentTime(0);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    setCurrentTime(videoRef.current?.currentTime || 0);
  };

  const handleLoadedMetadata = () => {
    setDuration(videoRef.current?.duration || 0);
  };

  const handleProgressClick = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const newTime = ratio * duration;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    videoRef.current.volume = val;
    setVolume(val);
    setIsMuted(val === 0);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const handleFullscreen = () => {
    const v = videoRef.current;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen();
  };

  const handleEnded = () => setIsPlaying(false);

  const progress = duration ? (currentTime / duration) * 100 : 0;
  const year = item.collection?.name?.match(/\d{4}/)?.[0] || item.year;

  return (
    <div className="arch-modal-overlay" onClick={onClose}>
      {showVideoPlayer && item.videoUrl && (
        <div className="arch-video-player-overlay" onClick={handleCloseVideo}>
          <div
            className="arch-video-player-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="arch-video-header">
              <span className="arch-video-title">
                {item.title}
                {year ? ` - [${year}]` : ""}
              </span>
              <button className="arch-video-close" onClick={handleCloseVideo}>
                ✕
              </button>
            </div>

            <video
              ref={videoRef}
              className="arch-video-element"
              src={item.videoUrl}
              autoPlay
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleEnded}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />

            <div className="arch-video-controls">
              <div
                className="arch-video-progress-bar"
                ref={progressRef}
                onClick={handleProgressClick}
              >
                <div
                  className="arch-video-progress-fill"
                  style={{ width: `${progress}%` }}
                />
                <div
                  className="arch-video-progress-dot"
                  style={{ left: `${progress}%` }}
                />
              </div>

              <div className="arch-video-controls-row">
                <button className="arch-video-btn" onClick={togglePlay}>
                  {isPlaying ? "⏸" : "▶"}
                </button>

                <span className="arch-video-time">
                  {formatTime(currentTime)}
                </span>

                <div className="arch-video-volume">
                  <button className="arch-video-btn" onClick={toggleMute}>
                    {isMuted || volume === 0 ? "🔇" : "🔊"}
                  </button>
                  <input
                    className="arch-video-volume-slider"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                  />
                </div>

                <button
                  className="arch-video-btn arch-video-fullscreen"
                  onClick={handleFullscreen}
                >
                  ⛶
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="arch-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="arch-modal-close" onClick={onClose}>
          × Close
        </button>

        <div className="arch-modal-media">
          {item.isPdf ? (
            <div
              onClick={handleMediaClick}
              style={{ cursor: "pointer", height: "100%" }}
            >
              <PdfViewer
                pdfUrl={item.pdfUrl}
                height="60vh"
                objectFit="contain"
              />
            </div>
          ) : item.isVideo && item.previewUrl ? (
            <img
              src={item.previewUrl}
              alt={item.title}
              className="arch-modal-img"
              draggable="false"
              onClick={handleMediaClick}
              style={{ cursor: "pointer" }}
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : (
            <img
              src={item.imageUrl}
              alt={item.title}
              className="arch-modal-img"
              draggable="false"
              onClick={handleMediaClick}
              style={{ cursor: "pointer" }}
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
        </div>

        <div className="arch-modal-right">
          <h2 className="arch-modal-title">{item.title}</h2>

          {item.metadata?.collection && (
            <p className="arch-modal-info">
              <strong>Collection:</strong> {item.metadata.collection}
            </p>
          )}

          <p className="arch-modal-info">
            <strong>Year:</strong>{" "}
            {item.collection?.name?.match(/\d{4}/)?.[0] || item.year || ""}
          </p>

          {item.isVideo && item.videoUrl && (
            <button
              className="arch-btn-watch-video"
              onClick={() => setShowVideoPlayer(true)}
            >
              ▶ Watch Video
            </button>
          )}

          {item.description && (
            <div className="pre-modal-desc">
              <Scrollbars autoHide style={{ height: "100%" }}>
                <p>{item.desc || item.description}</p>
              </Scrollbars>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchModal;
